import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { SearchFilters, type SearchFiltersState } from "@/components/venues/SearchFilters";
import { SortSelect } from "@/components/venues/SortSelect";
import { VenueCard } from "@/components/venues/VenueCard";
import { CityExplorer } from "@/components/venues/CityExplorer";
import { useVenues } from "@/hooks/useVenues";
import { useUserCity } from "@/hooks/useUserCity";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { GlobalSchema } from "@/components/seo/GlobalSchema";

const EMPTY_FILTERS: SearchFiltersState = {
  name: "",
  city: "",
  community: "",
  minCapacity: "",
  maxOfficialPrice: "",
  maxCommunityPrice: "",
  type: "",
  sortBy: "name",
};

const Index = () => {
  const [searchParams] = useSearchParams();
  const cityFromUrl = searchParams.get("city") || "";
  const resultsRef = useRef<HTMLDivElement>(null);
  const { city: userCity } = useUserCity();

  const [filters, setFilters] = useState<SearchFiltersState>({
    ...EMPTY_FILTERS,
    city: cityFromUrl,
  });
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(0);

  useSeoMeta({ pageType: hasSearched ? "search_results" : "home" });

  // Apply user city when detected (only if no manual search)
  useEffect(() => {
    if (userCity && !hasSearched && !cityFromUrl) {
      setFilters((f) => ({ ...f, city: userCity }));
    }
  }, [userCity, hasSearched, cityFromUrl]);

  useEffect(() => {
    if (cityFromUrl) {
      setFilters((f) => ({ ...f, city: cityFromUrl }));
      setPage(0);
      setHasSearched(true);
    }
  }, [cityFromUrl]);

  const activeCity = filters.city || userCity || "";
  const queryFilters = { ...filters, city: activeCity };

  const { data, isLoading } = useVenues(queryFilters, page);

  const handleFiltersChange = (newFilters: SearchFiltersState) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleSearch = () => {
    setHasSearched(true);
    setPage(0);
    resultsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const resultsTitle = hasSearched
    ? `${data?.totalCount ?? 0} sala${(data?.totalCount ?? 0) !== 1 ? "s" : ""} encontrada${(data?.totalCount ?? 0) !== 1 ? "s" : ""}`
    : activeCity
      ? `Salas en ${activeCity}`
      : "Todas las salas";

  // Numbered pagination
  const renderPagination = () => {
    if (!data || data.totalPages <= 1) return null;
    const pages: number[] = [];
    const total = data.totalPages;
    const current = page;

    // Show max 7 page numbers with ellipsis logic
    if (total <= 7) {
      for (let i = 0; i < total; i++) pages.push(i);
    } else {
      pages.push(0);
      if (current > 3) pages.push(-1); // ellipsis
      const start = Math.max(1, current - 1);
      const end = Math.min(total - 2, current + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (current < total - 4) pages.push(-1); // ellipsis
      pages.push(total - 1);
    }

    return (
      <div className="mt-8 flex items-center justify-center gap-1">
        {pages.map((p, idx) =>
          p === -1 ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">…</span>
          ) : (
            <Button
              key={p}
              variant={p === current ? "default" : "outline"}
              size="sm"
              className="min-w-[36px]"
              onClick={() => setPage(p)}
            >
              {p + 1}
            </Button>
          )
        )}
      </div>
    );
  };

  const currentPageType = hasSearched ? "search_results" : "home";

  return (
    <Layout>
      <GlobalSchema pageType={currentPageType} />
      {/* Hero with background image */}
      <section
        className="relative bg-foreground py-12 md:py-16"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=1920&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-foreground/75" />
        <div className="container relative z-10">
          <div className="text-center mb-8">
            <h1 className="font-display text-3xl font-bold tracking-tight text-primary-foreground md:text-5xl">
              Encuentra tu sala de conciertos
            </h1>
            <p className="mx-auto mt-3 max-w-xl text-primary-foreground/80 md:text-lg">
              El directorio colaborativo de salas de conciertos en España
            </p>
          </div>

          {/* Search card */}
          <div className="mx-auto max-w-3xl rounded-xl bg-background p-5 shadow-lg md:p-6">
            <SearchFilters filters={filters} onChange={handleFiltersChange} onSearch={handleSearch} />
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="container scroll-mt-24 py-8" ref={resultsRef}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">{resultsTitle}</h2>
          <SortSelect value={filters.sortBy} onChange={(v) => setFilters((f) => ({ ...f, sortBy: v }))} />
        </div>

        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-72 rounded-lg" />
            ))}
          </div>
        ) : data?.venues.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground">
            <p className="text-lg font-medium">No se encontraron salas</p>
            <p className="mt-1 text-sm">Prueba a ajustar los filtros de búsqueda</p>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {data?.venues.map((venue) => (
                <VenueCard key={venue.id} venue={venue} />
              ))}
            </div>
            {renderPagination()}
          </>
        )}
      </section>

      {/* City Explorer */}
      <CityExplorer />
    </Layout>
  );
};

export default Index;
